/** Random arcade-style display name, used when a player submits the name
 * field empty.
 *
 * WHY: the empty-field fallback used to be the literal strings "Player 1" /
 * "Player 2". Those read as placeholders rather than names, and they made
 * the leaderboard's team column meaningless ("Player 1" appears on every
 * anonymous row, so two unrelated teams are indistinguishable).
 *
 * CONSTRAINTS this list is built to satisfy:
 *  - Server-side `validateName` (server/src/util/validate.ts) allows 1-32
 *    VISIBLE chars. Longest pair below is well under that -- asserted in
 *    tests/unit/randomName.test.ts rather than eyeballed.
 *  - That same validator runs a conservative profanity screen which has a
 *    documented history of false positives on ordinary words (BUGLEDGER:
 *    "Mishit", "Shittu", "Matsushita"). Every word here is deliberately
 *    short, arcade-flavoured and free of embedded substrings that screen
 *    looks for -- a generated name that the server then REJECTS would turn
 *    "leave the field blank" into a hard failure, which is worse than the
 *    boring default it replaces.
 *  - No apostrophes/quotes/backslashes: these names are rendered on a
 *    canvas, in HTML, and stored in SQLite, and there is no reason to make
 *    any of those paths think about escaping.
 *
 * Deliberately NOT seeded/deterministic: this is a cosmetic client-side
 * label, never engine state. The engine's no-Math.random rule applies to
 * packages/engine (replay determinism); nothing here feeds the simulation
 * or the state hash.
 */

const ADJECTIVES = [
  'Neon',
  'Turbo',
  'Cosmic',
  'Retro',
  'Pixel',
  'Rapid',
  'Chrome',
  'Electric',
  'Midnight',
  'Golden',
  'Swift',
  'Arcade',
  'Quantum',
  'Vivid',
  'Lunar',
  'Solar',
  'Nimble',
  'Bold',
] as const;

const NOUNS = [
  'Muncher',
  'Chomper',
  'Runner',
  'Dodger',
  'Sprinter',
  'Wanderer',
  'Nomad',
  'Gobbler',
  'Racer',
  'Drifter',
  'Voyager',
  'Ranger',
  'Comet',
  'Rocket',
  'Falcon',
  'Phantom',
  'Maverick',
  'Pilot',
] as const;

/** Longest possible result, used by the test that guards the 32-char limit. */
export const MAX_GENERATED_NAME_LEN =
  Math.max(...ADJECTIVES.map((a) => a.length)) + 1 + Math.max(...NOUNS.map((n) => n.length));

function pick<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)]!;
}

/** e.g. "Neon Muncher". Space-separated so it reads as a name, not a handle. */
export function randomPlayerName(): string {
  return `${pick(ADJECTIVES)} ${pick(NOUNS)}`;
}
